const API_BASE = process.env.REACT_APP_API_URL || '';

export const IMPERSONATE_KEY = 'amk_impersonate_token';

// Admin "view as investor" sessions live in sessionStorage (this tab only)
export function getToken(): string | null {
  return sessionStorage.getItem(IMPERSONATE_KEY) || localStorage.getItem('amk_token');
}

export function getImpersonator(): string | null {
  const token = sessionStorage.getItem(IMPERSONATE_KEY);
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return payload.impersonator || null;
  } catch {
    return null;
  }
}

export function clearToken(): void {
  if (sessionStorage.getItem(IMPERSONATE_KEY)) {
    sessionStorage.removeItem(IMPERSONATE_KEY);
  } else {
    localStorage.removeItem('amk_token');
  }
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> || {}),
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, { ...options, headers });
  } catch {
    throw new Error('Network error — cannot reach server');
  }

  if (!res.ok) {
    let body: any = {};
    try {
      body = await res.json();
    } catch {
      // response wasn't JSON
    }

    if (res.status === 401 && path !== '/api/login') {
      clearToken();
      window.location.reload();
    }

    throw new Error(body.message || body.error || `Request failed (${res.status})`);
  }

  return res.json();
}

export const api = {
  login: (email: string, password: string) =>
    request<{ token: string }>('/api/login', {
      method: 'POST',
      body: JSON.stringify({ username: email, password }),
    }),

  getMe: () => request<any>('/api/me'),

  getPublicMarket: () =>
    request<{ symbol: string; price: number; change24h: number }[]>('/api/public/market'),

  getTrades: () => request<any>('/api/trades?limit=50'),

  getPortfolioStats: () => request<any>('/api/portfolio/stats'),

  getBalanceHistory: (period: string = 'ALL') =>
    request<any[]>(`/api/balance-history?period=${period}`),

  getWithdrawals: () => request<any>('/api/withdrawals'),

  getWithdrawalWindow: () =>
    request<{ open: boolean; opensAt: string; closesAt: string; timezone: string; availableBalance: number; savedWallet: string | null }>('/api/withdrawals/window'),

  createWithdrawal: (amount: number) =>
    request<any>('/api/withdrawals', {
      method: 'POST',
      body: JSON.stringify({ amount }),
    }),

  saveWithdrawalWallet: (walletAddress: string | null) =>
    request<{ savedWallet: string | null }>('/api/withdrawals/wallet', {
      method: 'PUT',
      body: JSON.stringify({ walletAddress }),
    }),

  getLivePrices: () => request<any[]>('/api/live-prices'),

  getDailyPnl: (month: number, year: number) =>
    request<{ month: number; year: number; days: Record<string, number> }>(`/api/daily-pnl?month=${month}&year=${year}`),

  getReferrals: () => request<any>('/api/referrals'),

  changePassword: (currentPassword: string, newPassword: string) =>
    request<any>('/api/settings/password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    }),

  get2faStatus: () => request<any>('/api/settings/2fa/status'),

  setup2fa: () => request<any>('/api/settings/2fa/setup', { method: 'POST' }),

  verify2fa: (code: string) =>
    request<any>('/api/settings/2fa/verify', {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),

  disable2fa: (password: string) =>
    request<any>('/api/settings/2fa/disable', {
      method: 'POST',
      body: JSON.stringify({ password }),
    }),

  getNotifications: () => request<any>('/api/notifications'),

  markNotificationsRead: () => request<any>('/api/notifications/read', { method: 'POST' }),
};
